import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WriteOnly } from '../components/ViewerGuard';
import { projectsApi, pipelineApi, targetsApi, formatApiError } from '../lib/api';
import { HelpButton } from '../components/HelpPanel';
import HowDoesThisWorkPill from '../components/HowDoesThisWorkPill';
import usePageTitle from '../hooks/usePageTitle';
import {
  Film, Calendar, CheckCircle, Clock, TrendingUp, Users,
  Plus, Pencil, Trash2, X, Loader2, Save, ChevronDown,
  Target, Zap, Mail, Flame, Snowflake, ArrowRight
} from 'lucide-react';

const statusConfig = {
  distribution: { label: 'Distribution', color: '#22C55E', icon: CheckCircle },
  development: { label: 'Development', color: '#00D4C8', icon: TrendingUp },
  financing: { label: 'Financing', color: '#FFD93D', icon: Clock },
  consulting: { label: 'Consulting', color: '#A3B5C7', icon: Users },
  active: { label: 'Active', color: '#00D4C8', icon: CheckCircle },
  complete: { label: 'Complete', color: '#22C55E', icon: CheckCircle },
  identified: { label: 'Identified', color: '#A3B5C7', icon: Clock },
  applied: { label: 'Applied', color: '#FFD93D', icon: Clock },
  awarded: { label: 'Awarded', color: '#22C55E', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#FF6B6B', icon: X },
};

const PROJECT_STATUSES = ['development', 'active', 'consulting', 'complete', 'identified', 'applied', 'awarded', 'rejected'];

const emptyForm = { name: '', category: 'Product', status: 'development', description: '', key_events: [] };

// Parent category color mapping
const parentColors = {
  'Products & Services': '#00D4C8',
  'Campaigns & Strategy': '#FFD93D',
  'Business Development': '#C8A97E',
  'Partnerships': '#33DDD3',
  'Research': '#A3B5C7',
  'Operations': '#9B8AFF',
};

const ProjectsPage = () => {
  usePageTitle('Projects');
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  // Project Targets state
  const [pipelineRecords, setPipelineRecords] = useState([]);
  const [allTargets, setAllTargets] = useState([]);

  const fetchProjects = async () => {
    try {
      const [projRes, catRes, pipeRes, targRes] = await Promise.all([
        projectsApi.getAll(),
        projectsApi.getCategories(),
        pipelineApi.getAll(),
        targetsApi.getAll(),
      ]);
      setProjects(projRes.data);
      setTaxonomy(catRes.data);
      setPipelineRecords(pipeRes.data);
      setAllTargets(targRes.data);
      if (!selectedProject && projRes.data.length > 0) setSelectedProject(projRes.data[0]);
    } catch (err) { console.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProjects(); }, []);

  const getStatusConfig = (status) => statusConfig[status] || statusConfig.active;

  const openCreate = () => {
    setEditingProject(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setForm({
      name: project.name || '',
      category: project.category || 'Product',
      status: project.status || 'development',
      description: project.description || '',
      key_events: project.key_events || [],
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingProject) {
        await projectsApi.update(editingProject.id, form);
      } else {
        await projectsApi.create(form);
      }
      setShowModal(false);
      await fetchProjects();
    } catch (err) { console.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (projectId) => {
    setDeleting(true);
    try {
      await projectsApi.delete(projectId);
      setShowDeleteConfirm(null);
      if (selectedProject?.id === projectId) setSelectedProject(null);
      await fetchProjects();
    } catch (err) { console.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  const addEvent = () => {
    if (!eventName.trim()) return;
    setForm(f => ({ ...f, key_events: [...f.key_events, { name: eventName, date: eventDate }] }));
    setEventName('');
    setEventDate('');
  };

  const removeEvent = (idx) => {
    setForm(f => ({ ...f, key_events: f.key_events.filter((_, i) => i !== idx) }));
  };

  // Get pipeline targets linked to a specific project
  const getProjectTargets = (projectId) => {
    return pipelineRecords
      .filter(pr => pr.project_ids?.includes(projectId) && pr.stage !== 'closed_lost')
      .map(pr => {
        const target = allTargets.find(t => t.id === pr.target_id);
        // Extract project-specific pitch angle from suggested_pitch_hooks
        let pitchAngle = null;
        if (target?.research_raw) {
          try {
            const js = target.research_raw.indexOf('{');
            const je = target.research_raw.lastIndexOf('}') + 1;
            if (js !== -1 && je > js) {
              const parsed = JSON.parse(target.research_raw.substring(js, je));
              const hooks = parsed?.suggested_pitch_hooks;
              if (Array.isArray(hooks) && hooks.length > 0) {
                // Find hook most relevant to this project's BD types
                const bdTypes = pr.bd_work_types || target?.bd_work_types || [];
                const match = hooks.find(h => bdTypes.some(bt => h.relationship_type?.toLowerCase().includes(bt.toLowerCase())));
                pitchAngle = match?.hook || hooks[0]?.hook;
              }
            }
          } catch {}
        }
        if (!pitchAngle) pitchAngle = target?.suggested_pitch_hook;
        return { ...pr, target, pitchAngle };
      });
  };

  const tempConfig = {
    hot: { color: '#FF6B6B', icon: Flame, label: 'Hot' },
    warm: { color: '#FFD93D', icon: TrendingUp, label: 'Warm' },
    cold: { color: '#6BCBFF', icon: Snowflake, label: 'Cold' },
    dormant: { color: '#6B7280', icon: Clock, label: 'Dormant' },
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="card-surface h-24 shimmer" />)}</div>
          <div className="lg:col-span-2 card-surface h-96 shimmer" />
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading text-3xl text-white" data-testid="projects-title">Projects</h1>
            <HelpButton />
            <HowDoesThisWorkPill feature="projects" />
          </div>
          <p className="text-sm text-[#A3B5C7]/70">Manage your active projects and products — all pipeline activity maps back here.</p>
        </div>
        <WriteOnly><button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-project-btn">
          <Plus className="w-4 h-4" /> Add Project
        </button></WriteOnly>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project List */}
        <div className="space-y-3" data-testid="projects-list">
          {projects.map((project, index) => {
            const config = getStatusConfig(project.status);
            const isSelected = selectedProject?.id === project.id;
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative group text-left p-4 rounded-lg border transition-all cursor-pointer ${
                  isSelected ? 'bg-[#0E1C33] border-[#00D4C8]/30 card-glow' : 'bg-[#0E1C33] border-[#1A2F4C] hover:border-[#00D4C8]/20'
                }`}
                onClick={() => setSelectedProject(project)}
                data-testid={`project-item-${project.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                    <Film className="w-5 h-5" style={{ color: config.color }} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium truncate ${isSelected ? 'text-[#00D4C8]' : 'text-white'}`}>{project.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide rounded" style={{ backgroundColor: `${config.color}15`, color: config.color }}>{config.label}</span>
                      {project.parent_category && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide rounded" style={{ backgroundColor: `${parentColors[project.parent_category] || '#A3B5C7'}15`, color: parentColors[project.parent_category] || '#A3B5C7' }}>{project.parent_category}</span>}
                      {project.category && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide rounded bg-[#1A2F4C] text-[#A3B5C7]">{project.category}</span>}
                    </div>
                  </div>
                  {/* Edit/Delete */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(project); }} className="p-1.5 rounded hover:bg-[#1A2F4C] text-[#A3B5C7] hover:text-[#00D4C8] transition-colors" title="Edit" data-testid={`edit-project-${project.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(project); }} className="p-1.5 rounded hover:bg-red-500/10 text-[#A3B5C7] hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-project-${project.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Project Detail */}
        {selectedProject && (
          <motion.div key={selectedProject.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 space-y-6" data-testid="project-detail">
            {/* Project Info Card */}
            <div className="card-surface p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl text-white mb-2">{selectedProject.name}</h2>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ backgroundColor: `${getStatusConfig(selectedProject.status).color}15`, color: getStatusConfig(selectedProject.status).color }}>
                    {React.createElement(getStatusConfig(selectedProject.status).icon, { className: "w-4 h-4", strokeWidth: 1.5 })}
                    {getStatusConfig(selectedProject.status).label}
                  </span>
                  {selectedProject.parent_category && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm" style={{ backgroundColor: `${parentColors[selectedProject.parent_category] || '#A3B5C7'}15`, color: parentColors[selectedProject.parent_category] || '#A3B5C7' }}>{selectedProject.parent_category}</span>}
                  {selectedProject.category && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-[#1A2F4C] text-[#A3B5C7]">{selectedProject.category}</span>}
                </div>
              </div>
              <WriteOnly><div className="flex items-center gap-2">
                <button onClick={() => openEdit(selectedProject)} className="btn-secondary text-sm flex items-center gap-1.5" data-testid="edit-selected-project-btn">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              </div></WriteOnly>
            </div>
            <div className="prose prose-invert max-w-none">
              <p className="text-[#D4E2F0] leading-relaxed">{selectedProject.description}</p>
            </div>
            {selectedProject.key_events?.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-4">Key Events</h3>
                <div className="space-y-3">
                  {selectedProject.key_events.map((event, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{event.name}</p>
                        <p className="text-sm text-[#A3B5C7]">{event.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-8 pt-6 border-t border-[#1A2F4C]">
              <div className="flex gap-3">
                <a href="/research" className="btn-primary">Research Targets for This Project</a>
                <a href="/pipeline" className="btn-secondary">View Pipeline</a>
              </div>
            </div>
            </div>

            {/* Project Targets Panel */}
            {(() => {
              const projectTargets = getProjectTargets(selectedProject.id);
              return (
                <div className="card-surface p-8" data-testid="project-targets-panel">
                  <div className="flex items-center gap-3 mb-6">
                    <Target className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl text-white">Project Targets</h3>
                    <span className="px-2 py-0.5 text-xs bg-[#00D4C8]/10 text-[#00D4C8] rounded-full border border-[#00D4C8]/20">{projectTargets.length}</span>
                  </div>
                  {projectTargets.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-[#A3B5C7]/60 text-sm mb-3">No pipeline targets linked to this project yet.</p>
                      <a href="/research" className="text-[#00D4C8] text-sm hover:underline">Research targets for {selectedProject.name}</a>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {projectTargets.map((pt) => {
                        const tc = tempConfig[pt.temperature] || tempConfig.cold;
                        const TempIcon = tc.icon;
                        const stageLabel = pt.stage?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        return (
                          <div key={pt.id} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] hover:border-[#00D4C8]/30 transition-colors" data-testid={`project-target-${pt.id}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <button onClick={() => navigate(`/targets/${pt.target_id}`)} className="text-white font-medium hover:text-[#00D4C8] transition-colors text-left truncate" data-testid={`target-link-${pt.target_id}`}>
                                    {pt.target_name || pt.target?.name || 'Unknown'}
                                  </button>
                                  <span className="text-[#A3B5C7] text-xs">{pt.target?.type}</span>
                                </div>
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide rounded" style={{ backgroundColor: `${tc.color}15`, color: tc.color }}>
                                    {stageLabel}
                                  </span>
                                  <span className="flex items-center gap-1 text-[10px] uppercase" style={{ color: tc.color }}>
                                    <TempIcon className="w-3 h-3" strokeWidth={1.5} />
                                    {tc.label}
                                  </span>
                                  {pt.owner && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#1A2F4C] text-[#A3B5C7] rounded">{pt.owner}</span>}
                                </div>
                                {pt.pitchAngle && (
                                  <p className="text-sm text-[#D4E2F0] italic border-l-2 border-[#00D4C8]/40 pl-3 mt-2 line-clamp-2">"{pt.pitchAngle}"</p>
                                )}
                              </div>
                              <WriteOnly>
                              <button
                                onClick={() => navigate(`/outreach?target=${pt.target_id}&project=${selectedProject.id}&hook=${encodeURIComponent(pt.pitchAngle || '')}`)}
                                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors border border-[#00D4C8]/20"
                                data-testid={`generate-outreach-${pt.id}`}
                              >
                                <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                                Generate Outreach
                              </button>
                              </WriteOnly>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </motion.div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="project-modal">
              <div className="p-6 border-b border-[#1A2F4C] flex items-center justify-between">
                <h2 className="font-heading text-xl text-white">{editingProject ? 'Edit Project' : 'Add Project'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-[#1A2F4C] text-[#A3B5C7]"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Name *</label>
                  <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="input-field" placeholder="Project name" data-testid="project-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Type</label>
                    <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="input-field" data-testid="project-type-select">
                      {taxonomy.map(group => (
                        <optgroup key={group.key} label={group.label}>
                          {group.types.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Status</label>
                    <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="input-field" data-testid="project-status-select">
                      {PROJECT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="input-field resize-none" placeholder="Project description" data-testid="project-description-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-2 block">Key Events</label>
                  {form.key_events.map((ev, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2 text-sm">
                      <span className="text-[#D4E2F0] flex-1">{ev.name}</span>
                      <span className="text-[#A3B5C7]">{ev.date}</span>
                      <button onClick={() => removeEvent(idx)} className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <input value={eventName} onChange={(e) => setEventName(e.target.value)} className="input-field text-sm flex-1" placeholder="Event name" />
                    <input type="month" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="input-field text-sm w-36" />
                    <button onClick={addEvent} className="btn-secondary text-xs px-3 py-2">Add</button>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-[#1A2F4C] flex justify-end gap-3">
                <button onClick={() => setShowModal(false)} className="btn-secondary text-sm">Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name.trim()} className="btn-primary text-sm flex items-center gap-2" data-testid="project-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingProject ? 'Save Changes' : 'Create Project'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowDeleteConfirm(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()} data-testid="delete-project-confirm-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg text-white">Delete {showDeleteConfirm.name}?</h3>
              </div>
              <p className="text-[#D4E2F0] text-sm mb-6">This will remove the project from your slate. Pipeline records and targets are not affected.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary text-sm">Cancel</button>
                <button onClick={() => handleDelete(showDeleteConfirm.id)} disabled={deleting} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-2" data-testid="confirm-delete-project-btn">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsPage;
